import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import { useRecorder } from "../../hooks/useRecorder";
import { usePostMessages } from "../../hooks/usePostMessages";

// Double-tap guard: the confirming tap is only accepted this long after arming.
const RELEASE_ARM_DELAY_MS = 2000;
// ...and an armed confirm decays back to safe: without this, any pause longer
// than the arm delay leaves a one-tap release live while the prompt is open.
const RELEASE_ARM_TIMEOUT_MS = 10000;

// Surfaces a NATIVE recording orphaned by a webview reset (marker present but
// nothing recording — see useRecorder's lostRecording state and the 2026-07-21
// incident in the design spec). Closing this modal does not release the guard
// against replacing an unresolved recording:
//   Try to recover  -> asks native to stop-and-hand-over (audio may survive)
//   Local Recordings -> opens native's Local Recordings screen + pokes its
//                       upload queue, keeping the marker until recovery; also
//                       stamps that the user checked, for the release below
//   I verified the transcript -> confirms an otherwise unidentifiable legacy
//                                recovery, after a successful save
//   No audio was recorded -> confirms an ambiguous native start failure was
//                            empty; never offered for other unresolved audio
//   No recording to recover -> after the rescue is spent and the user has
//                              recently checked Local Recordings, a two-tap
//                              confirmation (an armed tap decays back to safe)
//                              that the phone has no file; stops the guard
//                              only (nothing sent to native, nothing deleted)
//   Close           -> hides the prompt and keeps the marker
const formatWhen = (ts) => {
  const ms = Number(ts) || 0;
  if (!ms) return "";
  try {
    const d = new Date(ms);
    if (d.toDateString() === new Date().toDateString()) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

function ModalLostRecording() {
  const { t } = useTranslation();
  const {
    lostRecording,
    lostRecordingState,
    attemptLostRecordingRescue,
    confirmLostRecordingRecovered,
    confirmNativeStartFailed,
    confirmNoRecordingToRecover,
    stampLocalRecordingsChecked,
    hasLocalRecordingsEvidence,
    dismissLostRecording,
    recording,
    startingRecording,
  } = useRecorder();
  const { sendShowRecordings, sendRequestSync } = usePostMessages();
  const [releaseArmed, setReleaseArmed] = useState(false);
  const [releaseReady, setReleaseReady] = useState(false);
  const [localRecordingsChecked, setLocalRecordingsChecked] = useState(false);

  // Any close or re-surface (a new lostRecording object) disarms the release
  // and re-reads the evidence — in an effect, not during render: the evidence
  // read touches storage (and peek can self-heal it with a write).
  useEffect(() => {
    setReleaseArmed(false);
    setReleaseReady(false);
    setLocalRecordingsChecked(!!lostRecording?.recordingUuid && hasLocalRecordingsEvidence(lostRecording.recordingUuid));
    // eslint-disable-next-line
  }, [lostRecording]);

  useEffect(() => {
    if (!releaseArmed) return;
    const ready = setTimeout(() => setReleaseReady(true), RELEASE_ARM_DELAY_MS);
    const disarm = setTimeout(() => {
      setReleaseArmed(false);
      setReleaseReady(false);
    }, RELEASE_ARM_TIMEOUT_MS);
    return () => {
      clearTimeout(ready);
      clearTimeout(disarm);
    };
  }, [releaseArmed]);

  if (!lostRecording) return null;

  const rescuing = lostRecordingState === "rescuing";
  const rescueSpent = lostRecordingState !== "idle";
  const needsVerification = !!lostRecording.recoveryCandidate;
  const startFailed = !needsVerification && !!lostRecording.startFailed && !lostRecording.stoppedAt;
  let titleKey = "hooks.useRecorder.lostRecordingTitle";
  let bodyKey = "hooks.useRecorder.lostRecordingBody";
  if (needsVerification) {
    titleKey = "hooks.useRecorder.lostRecordingVerifyTitle";
    bodyKey = "hooks.useRecorder.lostRecordingVerifyBody";
  } else if (startFailed) {
    titleKey = "hooks.useRecorder.lostRecordingStartFailedTitle";
    bodyKey = "hooks.useRecorder.lostRecordingStartFailedBody";
  }
  const when = formatWhen(lostRecording.startedAt) || t("hooks.useRecorder.lostRecordingUnknownTime");

  // The hook refuses any startFailed marker and can never match a marker with
  // no UUID (a legacy uuid-less handoff), so neither may be offered a button
  // that cannot work. The evidence itself lives in the hook (persistent stamp
  // or session fallback) so this gate and the hook's guard cannot disagree.
  const offerRelease =
    !needsVerification && !lostRecording.startFailed && !!lostRecording.recordingUuid && lostRecordingState === "notRecovered";

  const openLocalRecordings = () => {
    // Opening the native screen and requesting sync do not confirm recovery.
    // Hide the prompt while keeping the unresolved recording's marker.
    stampLocalRecordingsChecked(lostRecording.recordingUuid);
    sendShowRecordings();
    sendRequestSync();
    dismissLostRecording();
  };

  const onRelease = () => {
    if (!releaseArmed) {
      setReleaseArmed(true);
      return;
    }
    if (!releaseReady) return;
    setReleaseArmed(false);
    setReleaseReady(false);
    confirmNoRecordingToRecover();
  };

  return (
    <Modal
      show={!!lostRecording}
      title={t(titleKey)}
      close={dismissLostRecording}
      action={
        <>
          {needsVerification ? (
            <button onClick={confirmLostRecordingRecovered} disabled={rescuing || recording || startingRecording}>
              {t("hooks.useRecorder.lostRecordingConfirmRecovered")}
            </button>
          ) : startFailed ? (
            <button onClick={confirmNativeStartFailed} disabled={rescuing || recording || startingRecording}>
              {t("hooks.useRecorder.lostRecordingConfirmEmptyStart")}
            </button>
          ) : (
            <button onClick={attemptLostRecordingRescue} disabled={rescueSpent || recording || startingRecording}>
              {rescuing ? t("hooks.useRecorder.lostRecordingRecovering") : t("hooks.useRecorder.lostRecordingTryRecover")}
            </button>
          )}
          {offerRelease && (
            <button
              className="red"
              onClick={onRelease}
              disabled={!localRecordingsChecked || recording || startingRecording || (releaseArmed && !releaseReady)}
            >
              {releaseArmed ? t("hooks.useRecorder.lostRecordingReleaseArm") : t("hooks.useRecorder.lostRecordingRelease")}
            </button>
          )}
          <button className="white" onClick={openLocalRecordings} disabled={rescuing}>
            {t("hooks.useRecorder.lostRecordingCheckLocal")}
          </button>
          <button className="white" onClick={dismissLostRecording} disabled={rescuing}>
            {t("hooks.useRecorder.lostRecordingDismiss")}
          </button>
        </>
      }
    >
      <p>
        {t(bodyKey, {
          when,
        })}
      </p>
      <p>
        {t(startFailed ? "hooks.useRecorder.lostRecordingStartFailedBlocked" : "hooks.useRecorder.lostRecordingStartBlocked")}
      </p>
      {!needsVerification && !startFailed && lostRecordingState === "notRecovered" && (
        <p className="danger">{t("hooks.useRecorder.lostRecordingNotRecovered")}</p>
      )}
      {offerRelease && <p>{t("hooks.useRecorder.lostRecordingNoAudioHint", { when })}</p>}
      {offerRelease && releaseArmed && <p className="danger">{t("hooks.useRecorder.lostRecordingReleaseWarning", { when })}</p>}
    </Modal>
  );
}

export default ModalLostRecording;
